import { DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';
import { Button, DashedTag } from '@praetorian-chariot/ui';
import {
  setUpTOTP,
  updateMFAPreference,
  verifyTOTPSetup,
} from 'aws-amplify/auth';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Modal } from '@/components/Modal';
import { ViewRecoveryCodesModal } from '@/components/ViewRecoveryCodesModal';
import { MFASetupModal } from '@/sections/signup/MFASetup';
import { useAuth } from '@/state/auth';

interface TwoFactorAuthSectionProps {
  disabled?: boolean;
  variant?: 'expanded' | 'compact';
  mfaEnabled?: boolean;
  onMfaEnabledChange?: (enabled: boolean) => void;
  className?: string;
  showHeader?: boolean;
  'data-testid'?: string;
}

export function TwoFactorAuthSection({
  disabled = false,
  variant = 'expanded',
  mfaEnabled: externalMfaEnabled,
  onMfaEnabledChange,
  className = '',
  showHeader = true,
  'data-testid': testId,
}: TwoFactorAuthSectionProps) {
  const { setTotpSecretDetails, setIsLoading, ssoDomain, isPraetorianUser } =
    useAuth();
  const [internalMfaEnabled, setInternalMfaEnabled] = useState(false);
  const [mfaSetupModalOpen, setMfaSetupModalOpen] = useState(false);
  const [showMFASuccessModal, setShowMFASuccessModal] = useState(false);
  const [viewRecoveryCodesModalOpen, setViewRecoveryCodesModalOpen] =
    useState(false);

  // Use external mfaEnabled prop if provided, otherwise use internal state
  const mfaEnabled =
    externalMfaEnabled !== undefined ? externalMfaEnabled : internalMfaEnabled;

  const setupTOTPFn = async () => {
    const totpSetupDetails = await setUpTOTP();
    setTotpSecretDetails(totpSetupDetails);
    setMfaSetupModalOpen(true);
  };

  async function verifyMFASettings(code: string) {
    try {
      setIsLoading(true);
      await verifyTOTPSetup({ code });
      await updateMFAPreference({ totp: 'PREFERRED' });
      setMfaSetupModalOpen(false);

      // Update MFA enabled state
      if (onMfaEnabledChange) {
        onMfaEnabledChange(true);
      } else {
        setInternalMfaEnabled(true);
      }

      // Show success modal for 3 seconds
      setShowMFASuccessModal(true);
      setTimeout(() => {
        setShowMFASuccessModal(false);
      }, 3000);
    } catch (error) {
      toast.error(`Error verifying TOTP setup: ${error}`);
    } finally {
      setIsLoading(false);
    }
  }

  // Expanded variant shows both authenticator and recovery codes as separate sections
  if (variant === 'expanded') {
    return (
      <>
        <div
          className={`flex flex-col gap-4 ${className}`}
          data-testid={testId}
        >
          {showHeader && (
            <div>
              <h4 className="mb-1 font-bold">2FA Method</h4>
              <span className="text-chariot-text-secondary text-xs">
                This will appear by default when you log in.
              </span>
            </div>
          )}

          {/* Authenticator App */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex size-6 items-center justify-center">
                <DevicePhoneMobileIcon className="text-chariot-text-secondary size-5" />
              </div>
              <span className="font-bold">Authenticator App</span>
            </div>
            <Button
              label="Change 2FA"
              styleType="primary"
              size="sm"
              onClick={() => setupTOTPFn()}
              disabled={disabled}
            />
          </div>

          {/* Recovery Codes Section */}
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <h4 className="font-bold">Recovery Codes</h4>
              <span className="text-chariot-text-secondary text-xs">
                Use your recovery codes to log in if you lose your device, lose
                your security key, or if you can&apos;t receive your
                verification code through an authenticator app.
              </span>
            </div>
            <Button
              label="View Codes"
              styleType="primary"
              size="sm"
              onClick={() => setViewRecoveryCodesModalOpen(true)}
              disabled={disabled}
            />
          </div>
        </div>

        {/* Modals */}
        <MFASetupModal
          open={mfaSetupModalOpen}
          onClose={() => setMfaSetupModalOpen(false)}
          onVerifyMFA={verifyMFASettings}
        />

        <ViewRecoveryCodesModal
          open={viewRecoveryCodesModalOpen}
          onClose={() => setViewRecoveryCodesModalOpen(false)}
        />

        {/* MFA Success Celebration Modal */}
        <Modal
          open={showMFASuccessModal}
          onClose={() => setShowMFASuccessModal(false)}
          title=""
          hideClose
          hideFooter
          className="relative overflow-hidden"
          style="dialog"
        >
          {/* Confetti Background */}
          <div
            className="absolute inset-x-0 top-0 h-[30%] bg-cover bg-center opacity-20"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='confetti' x='0' y='0' width='20' height='20' patternUnits='userSpaceOnUse'%3E%3Crect x='2' y='2' width='3' height='8' fill='%23ff6b6b' transform='rotate(15 3.5 6)'/%3E%3Crect x='12' y='5' width='2' height='6' fill='%234ecdc4' transform='rotate(-20 13 8)'/%3E%3Crect x='7' y='12' width='4' height='2' fill='%23ffe66d' transform='rotate(45 9 13)'/%3E%3Crect x='15' y='15' width='2' height='4' fill='%23ff6b6b' transform='rotate(-30 16 17)'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100' height='100' fill='url(%23confetti)'/%3E%3C/svg%3E")`,
            }}
          />

          {/* Modal Content */}
          <div className="relative z-10 flex flex-col gap-4 px-8 py-12">
            <h1 className="text-chariot-text-primary text-3xl font-bold">
              You&apos;re all set!
              <br /> MFA is now Enabled.
            </h1>
            <p className="text-chariot-text-secondary text-sm">
              To change your MFA settings, please contact{' '}
              <span className="text-chariot-brand-primary">
                support@praetorian.com.
              </span>
            </p>
          </div>
        </Modal>
      </>
    );
  }

  // Compact variant - single section that changes based on MFA state
  return (
    <>
      <div
        className={`border-chariot-stroke rounded-md border p-6 ${className}`}
        data-testid={testId}
      >
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <h2 className="text-chariot-text-primary text-lg font-bold">
              Two-Factor Authentication
            </h2>
            <p className="text-chariot-text-secondary text-sm">
              Add an extra layer of security to your account
            </p>
          </div>
          <div className="flex items-center gap-3">
            {mfaEnabled && (
              <DashedTag
                label="Enabled"
                className="text-chariot-status-success"
              />
            )}
            <Button
              label={mfaEnabled ? 'View Recovery Codes' : 'Enable 2FA'}
              styleType={mfaEnabled ? 'secondary' : 'primary'}
              size="sm"
              onClick={() => {
                if (mfaEnabled) {
                  setViewRecoveryCodesModalOpen(true);
                } else {
                  setupTOTPFn();
                }
              }}
              startIcon={<DevicePhoneMobileIcon className="size-4" />}
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      {mfaSetupModalOpen && (
        <MFASetupModal
          open={mfaSetupModalOpen}
          onClose={() => setMfaSetupModalOpen(false)}
          onVerifyMFA={verifyMFASettings}
        />
      )}

      <ViewRecoveryCodesModal
        open={viewRecoveryCodesModalOpen}
        onClose={() => setViewRecoveryCodesModalOpen(false)}
      />

      {/* MFA Success Modal */}
      <AnimatePresence>
        {showMFASuccessModal && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-chariot-background-primary rounded-lg p-8"
            >
              <div className="flex flex-col items-center gap-4">
                <CheckBadgeIcon className="text-chariot-status-success size-16" />
                <h2 className="text-2xl font-bold">
                  2FA Enabled Successfully!
                </h2>
                <p className="text-chariot-text-secondary">
                  Your account is now protected with two-factor authentication.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
